import * as THREE from 'three';
import { serverNow } from '../services/worldSocket';
import { thunder } from '../game/sfx';
import { SLICE_DISTRICT } from '../config';

// ── WEATHER — OAKLAND'S, LIVE ────────────────────────────────────────────────
//
// HypeTown is in Oakland, so its weather is Oakland's: what the National
// Weather Service's station at Oakland airport (SLICE_DISTRICT.weatherStation)
// actually observed in the last few minutes. If it is raining in Oakland it is
// raining in the world; if it is clear there, it is clear here. Nothing is made
// up — with no reading (offline, station silent for 90 minutes) there is no
// rain, and the sky keeps the hour's own clouds.
//
// The feed: api.weather.gov — free, keyless, public domain, open to browsers
// (CORS *), edge-cached for five minutes. The station reports every five; the
// world asks every ten, and again when a tab that sat hidden comes back. At
// load it reads the last ~90 minutes too, so streets that it rained on half an
// hour ago are still wet when you arrive.
//
// What a reading drives:
//   · rain / drizzle / hail → the rain, drawn (Rain.jsx) and heard (sfx);
//   · thunderstorms (overhead or in the vicinity) → lightning and thunder;
//   · the cloud layers (FEW / SCT / BKN / OVC) → the cloud deck; overcast
//     hides the sun, flattens the light and greys the sky — the marine layer;
//   · visibility → fog;
//   · every flat surface gets WET while it rains, and stays wet a while after.
//
// Applied ON TOP of the hour's keyframes (skyState), every frame, from fresh
// values — nothing accumulates.
//
// ?weather=rain | storm | fog | overcast | clear pins it and skips the feed
// (`clear` is the art-directed sky: no rain, the hour's own clouds).
// window.__look.rain = 0..1 in dev.

export const weather = {
  rain: 0,       // 0..1, how hard it is raining right now
  wet: 0,        // 0..1, how wet the ground is (lags `rain`)
  storm: false,
  flash: 0,      // lightning, 0..1, decays fast
  cover: 0.35,   // 0..1, the cloud deck
  overcast: 0,   // 0..1, how much of a lid the deck is (sun hidden, light flat)
  fog: 0,        // 0..1, from visibility
  kind: 'clear', // clear | rain | storm | fog — what the HUD clock says
  live: null,    // the last reading, for the curious: { at, text, rain, storm, cover, fog }
};

const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('weather') : null;

// ── READING AN OBSERVATION ───────────────────────────────────────────────────
// A METAR weather token — "-RA", "+TSRA", "VCSH", "DZ", "BR" — read for what
// the world can draw. Intensity: "-" light, none moderate, "+" heavy.
const PRECIP = [
  ['RA', [0.38, 0.65, 0.95]],   // rain (also SHRA showers, FZRA freezing)
  ['DZ', [0.18, 0.28, 0.4]],    // drizzle
  ['GR', [0.6, 0.8, 1]],        // hail
  ['GS', [0.5, 0.7, 0.9]],      // small hail
  ['UP', [0.3, 0.5, 0.8]],      // precipitation the sensor couldn't name
];
function readToken(raw) {
  const t = String(raw || '').toUpperCase().replace(/\s+/g, '');
  if (!t) return { rain: 0, storm: false };
  const k = t[0] === '+' ? 2 : t[0] === '-' ? 0 : 1;
  const nearby = t.includes('VC');   // in the vicinity — not falling here
  let rain = 0;
  if (!nearby) for (const [code, by] of PRECIP) if (t.includes(code)) rain = Math.max(rain, by[k]);
  return { rain, storm: t.includes('TS') };
}

// Sky cover by METAR amount. CLR is an automated station's "nothing below
// 12,000 ft" — it cannot see higher, so a few wisps are allowed.
const COVER = { SKC: 0, CLR: 0.12, FEW: 0.26, SCT: 0.42, BKN: 0.68, OVC: 0.95, VV: 0.95 };

/** One NWS observation (`properties`) → what the world draws. null if unusable. */
export function readObservation(p) {
  if (!p || !p.timestamp) return null;
  const at = Date.parse(p.timestamp);
  if (!Number.isFinite(at)) return null;
  let rain = 0; let storm = false;
  for (const w of p.presentWeather || []) {
    // The decoded token is the reliable part; the enum is the fallback for a
    // token the decoder left blank.
    const raw = w.rawString || `${w.intensity === 'light' ? '-' : w.intensity === 'heavy' ? '+' : ''}${w.inVicinity ? 'VC' : ''}${w.weather === 'thunderstorms' ? 'TS' : w.weather === 'rain' ? 'RA' : w.weather === 'drizzle' ? 'DZ' : w.weather === 'hail' ? 'GR' : ''}`;
    const r = readToken(raw);
    rain = Math.max(rain, r.rain);
    storm = storm || r.storm;
  }
  let cover = null;
  for (const c of p.cloudLayers || []) {
    const v = COVER[c?.amount];
    if (v != null) cover = Math.max(cover ?? 0, v);
  }
  if (storm) cover = Math.max(cover ?? 0, 0.85);
  if (rain > 0) cover = Math.max(cover ?? 0, 0.8);
  const vis = p.visibility?.value;
  // Ten miles is "unlimited"; haze starts to show under 8 km, fog under 1.
  const fog = Number.isFinite(vis) ? THREE.MathUtils.clamp((8000 - vis) / 7000, 0, 1) : 0;
  return { at, text: p.textDescription || '', rain, storm, cover, fog };
}

/** How wet the ground gets under a given rain — even a drizzle soaks a street. */
const wetFor = (rain) => (rain > 0.05 ? 0.55 + 0.45 * rain : 0);

// ── THE FEED ─────────────────────────────────────────────────────────────────
const FEED = 'https://api.weather.gov/stations/';
const POLL_MS = 10 * 60 * 1000;
const STALE_MS = 90 * 60 * 1000;
const DRY_MIN = 45;               // a soaked street dries in about this many minutes
const live = { ok: false, at: 0, rain: 0, storm: false, cover: null, fog: 0, text: '' };
let feedStarted = false;
let lastAsk = 0;
let seedWet = null;               // set by the first reading; applied on the next frame

async function getJson(path) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(FEED + path, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`weather ${res.status}`);
    return await res.json();
  } finally { clearTimeout(t); }
}

function take(r) {
  if (!r) return;
  Object.assign(live, r, { ok: true });
  weather.live = { ...r };
}

async function ask(first) {
  const station = SLICE_DISTRICT.weatherStation;
  if (!station) return;
  lastAsk = Date.now();
  try {
    if (first) {
      // The last ~90 minutes: the newest reading is now; the rest say whether
      // the streets are still wet from rain that has stopped.
      const d = await getJson(`${station}/observations?limit=18`);
      const obs = (d?.features || []).map((f) => readObservation(f?.properties)).filter(Boolean).sort((a, b) => b.at - a.at);
      if (!obs.length) return;
      take(obs[0]);
      const now = serverNow();
      let wet = wetFor(obs[0].rain);
      for (const o of obs) {
        if (o.rain <= 0) continue;
        const mins = (now - o.at) / 60000;
        wet = Math.max(wet, wetFor(o.rain) * Math.max(0, 1 - mins / DRY_MIN));
      }
      seedWet = wet;
    } else {
      const d = await getJson(`${station}/observations/latest`);
      take(readObservation(d?.properties));
    }
  } catch { /* keep the last good reading until it goes stale */ }
}

function startFeed() {
  feedStarted = true;
  if (typeof window === 'undefined' || !SLICE_DISTRICT.weatherStation) return;
  ask(true);
  setInterval(() => { if (!document.hidden) ask(false); }, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && Date.now() - lastAsk > POLL_MS) ask(false);
  });
}

// ── EVERY FRAME ──────────────────────────────────────────────────────────────
let nextFlash = 0;
let flashLeft = 0;
let coverInit = false;
const grey = new THREE.Color();
const tmp = new THREE.Color();
const desat = (c, k) => { const l = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722; grey.setRGB(l, l, l); c.lerp(grey, k); return c; };

/** Advance the weather and lay it over the evaluated sky. */
export function applyWeather(sky, skyU, hour, dt, elapsed) {
  const hourCover = skyU.uCloudCover.value;   // the keyframes' art-directed deck
  // Target.
  let target = 0; let storm = false; let cover = hourCover; let fog = 0;
  const dev = typeof window !== 'undefined' ? window.__look : null;
  if (dev?.rain != null) { target = dev.rain; storm = target > 0.85; }
  else if (q === 'rain') target = 0.8;
  else if (q === 'storm') { target = 1; storm = true; }
  else if (q === 'fog') { fog = 0.85; cover = 0.95; }
  else if (q === 'overcast') cover = 0.95;
  else if (q === 'clear') target = 0;
  else {
    if (!feedStarted) startFeed();
    if (live.ok && serverNow() - live.at < STALE_MS) {
      target = live.rain; storm = live.storm; fog = live.fog;
      if (live.cover != null) cover = live.cover;
    }
  }
  if (seedWet != null) { weather.wet = seedWet; seedWet = null; }
  if (target > 0 || storm) cover = Math.max(cover, target > 0 ? 0.8 : 0.85);

  // Rain eases in and out over a few seconds; clouds and fog roll in over
  // several; the ground soaks in over a minute or two and dries over ~45.
  weather.rain += (target - weather.rain) * Math.min(1, dt * 0.6);
  if (!coverInit) { weather.cover = cover; coverInit = true; }
  weather.cover += (cover - weather.cover) * Math.min(1, dt * 0.15);
  weather.fog += (fog - weather.fog) * Math.min(1, dt * 0.15);
  const wetTarget = wetFor(target);
  const wetRate = wetTarget > weather.wet ? 0.012 : 1 / (DRY_MIN * 60);   // per second
  weather.wet += Math.max(-wetRate * dt, Math.min(wetRate * dt, wetTarget - weather.wet));
  weather.storm = storm;
  weather.overcast = THREE.MathUtils.smoothstep(weather.cover, 0.55, 0.95);
  weather.kind = weather.storm ? 'storm' : weather.rain >= 0.05 ? 'rain' : weather.fog > 0.5 ? 'fog' : 'clear';

  // Lightning. By day a strike lights less of a sky that is already lit.
  if (weather.storm && elapsed > nextFlash) {
    nextFlash = elapsed + 5 + Math.random() * 14;
    flashLeft = 2 + Math.floor(Math.random() * 2);
    weather.flash = 1;
    setTimeout(() => thunder(0.6 + Math.random() * 0.4), 900 + Math.random() * 2200);
  }
  if (weather.flash > 0) {
    weather.flash = Math.max(0, weather.flash - dt * 7);
    // A real strike flickers: a couple of re-strikes as it fades.
    if (weather.flash < 0.35 && flashLeft > 1 && Math.random() < 0.25) { weather.flash = 0.8; flashLeft -= 1; }
  }

  // What it does to the picture. `g` is how grey the day is: rain all the way,
  // a solid overcast or a fog most of the way.
  const r = weather.rain;
  const fg = weather.fog;
  const g = Math.max(r, 0.6 * weather.overcast, 0.5 * fg);
  skyU.uCloudCover.value = THREE.MathUtils.lerp(weather.cover, 0.97, r);
  if (g > 0.001) {
    sky.lightI *= 1 - 0.72 * g;
    skyU.uSunVis.value *= 1 - 0.92 * g;
    // Cloud shade is built from the clear sky's blue, which under a full deck
    // reads as gaps of blue sky. Grey it — but less at night, when the deck's
    // underside is lit orange by the city and that is the look.
    const cg = g * (1 - 0.7 * sky.night);
    desat(skyU.uCloudLit.value, 0.5 * cg).multiplyScalar(1 - 0.5 * g);
    desat(skyU.uCloudDark.value, 0.7 * cg).multiplyScalar(1 - 0.45 * g);
    // Seen from under, a solid deck is one soft grey, not separate clouds.
    skyU.uCloudDark.value.lerp(skyU.uCloudLit.value, 0.3 * weather.overcast);
    desat(skyU.uZenith.value, 0.6 * g).multiplyScalar(1 - 0.38 * g);
    desat(skyU.uHorizon.value, 0.55 * g).multiplyScalar(1 - 0.3 * g);
    // Fog is water in the air: no colour of its own.
    desat(sky.fogColor, Math.min(1, 0.55 * g + 0.4 * fg)).multiplyScalar(1 - 0.12 * g);
    skyU.uGround.value.copy(sky.fogColor).multiplyScalar(0.55);
    desat(sky.hemiSky, 0.5 * g);
    sky.hemiI *= 1 - 0.1 * g;
    sky.saturation *= 1 - 0.12 * g;
  }
  // Rain and fog thicken the air; a high lid of cloud doesn't.
  sky.haze *= 1 + 2.2 * r + 3.5 * fg;
  skyU.uStars.value *= 1 - Math.max(r, weather.overcast, fg);
  if (weather.flash > 0) {
    const f = weather.flash * (0.45 + 0.55 * sky.night);
    sky.ambI += f * 1.4;
    sky.ambColor.lerp(tmp.set('#dfe6ff'), Math.min(1, f));
    sky.hemiI += f * 0.8;
    skyU.uZenith.value.lerp(tmp.set('#b8c4e8'), f * 0.7);
    skyU.uCloudDark.value.lerp(tmp.set('#9aa4c8'), f * 0.8);
    skyU.uCloudLit.value.lerp(tmp.set('#e8ecff'), f * 0.8);
  }
}
